import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Loader2, UserCheck, UserX, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  useMembershipRequestPreview,
  useAcceptMembershipRequest,
  useDeclineMembershipRequest,
} from "@/hooks/useCompanies";

export default function MembershipRequestPreview() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
  const [declineMode, setDeclineMode] = useState(false);
  const [declineMessage, setDeclineMessage] = useState("");
  const [done, setDone] = useState<"accepted" | "declined" | null>(null);

  const { data: req, isLoading, isError } = useMembershipRequestPreview(token);
  const accept = useAcceptMembershipRequest();
  const decline = useDeclineMembershipRequest();

  function handleAccept() {
    if (!token) return;
    accept.mutate(token, {
      onSuccess: () => {
        setDone("accepted");
        toast.success("Membro adicionado com sucesso!");
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Erro ao aceitar solicitação.";
        if (msg.includes("não está pendente") || msg.includes("já é membro")) {
          setDone("accepted");
        } else {
          toast.error(msg);
        }
      },
    });
  }

  function handleDecline() {
    if (!token) return;
    decline.mutate(
      { token, message: declineMessage.trim() || undefined },
      {
        onSuccess: () => {
          setDone("declined");
          toast.info("Solicitação recusada.");
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "Erro ao recusar solicitação.";
          if (msg.includes("não está pendente")) {
            setDone("declined");
          } else {
            toast.error(msg);
          }
        },
      },
    );
  }

  if (authLoading || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !req) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>Solicitação não encontrada</CardTitle>
            <CardDescription>Este link é inválido ou já foi processado.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (req.status !== "pending" || done) {
    const wasAccepted = done === "accepted" || req.status === "accepted";
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            {wasAccepted ? (
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-2" />
            ) : (
              <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
            )}
            <CardTitle>
              {wasAccepted ? "Solicitação aceita" : "Solicitação recusada"}
            </CardTitle>
            <CardDescription>
              {wasAccepted
                ? `${req.requester_name} agora é membro de ${req.company_name}.`
                : `A solicitação de ${req.requester_name} foi recusada.`}
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Ir para o painel
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <CardTitle>Login necessário</CardTitle>
            <CardDescription>
              Você precisa estar logado como admin de <strong>{req.company_name}</strong> para revisar esta solicitação.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button onClick={() => loginWithRedirect({ appState: { returnTo: `/solicitar-membro/${token}` } })}>
              Fazer login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Solicitação de acesso</CardTitle>
          <CardDescription>
            Empresa: <strong>{req.company_name}</strong>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Solicitante</span>
              <span className="font-semibold">{req.requester_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Email</span>
              <span className="font-mono text-xs">{req.requester_email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Data</span>
              <span>{new Date(req.created_at).toLocaleDateString("pt-BR")}</span>
            </div>
          </div>

          {declineMode && (
            <div className="space-y-2">
              <Label htmlFor="decline-msg">Mensagem para o solicitante (opcional)</Label>
              <Textarea
                id="decline-msg"
                placeholder="Motivo da recusa..."
                value={declineMessage}
                onChange={(e) => setDeclineMessage(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-2 justify-end">
          {declineMode ? (
            <>
              <Button variant="ghost" onClick={() => setDeclineMode(false)}>
                Voltar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
                disabled={decline.isPending}
                className="gap-2"
              >
                {decline.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserX className="h-4 w-4" />
                )}
                Confirmar recusa
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setDeclineMode(true)}
                className="gap-2 text-destructive hover:text-destructive"
              >
                <UserX className="h-4 w-4" />
                Recusar
              </Button>
              <Button
                onClick={handleAccept}
                disabled={accept.isPending}
                className="gap-2"
              >
                {accept.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserCheck className="h-4 w-4" />
                )}
                Aceitar
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
